import { useCallback, useEffect, useMemo, useState } from 'react'
import { getTopOf, scrollToY } from 'utils/scroll'
import { isMobile } from 'utils/userAgent'

type SortDirection = 'asc' | 'desc'

interface SortState<F extends string> {
  field: F
  direction: SortDirection
}

interface UseSortableTableOptions<T, F extends string> {
  data?: T[]
  defaultSortField: F
  comparators: Record<F, (a: T, b: T) => number>
  pageSize: number
  initialDirection?: SortDirection

  enableScrollOnPageChange?: boolean
  mobileSelector?: string
  mobileOffset?: number
  breakpoint?: number
  scrollBehavior?: ScrollBehavior
}

export function useSortableTable<T, F extends string>({
  data,
  defaultSortField,
  comparators,
  pageSize,
  initialDirection = 'desc',

  enableScrollOnPageChange = true,
  mobileSelector = '#table-section',
  mobileOffset = 64,
  breakpoint = 768,
  scrollBehavior = 'auto',
}: UseSortableTableOptions<T, F>) {
  const [sortState, setSortState] = useState<SortState<F>>({
    field: defaultSortField,
    direction: initialDirection,
  })
  const [page, setPage] = useState(1)

  const safeData = data ?? []
  const totalItems = safeData.length
  const maxPage = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1

  useEffect(() => {
    setPage(1)
  }, [totalItems, pageSize])

  useEffect(() => {
    setPage((prev) => Math.min(prev, maxPage))
  }, [maxPage])

  const sortedData = useMemo(() => {
    if (safeData.length === 0) return [] as T[]

    const comparator = comparators[sortState.field]
    if (!comparator) return safeData.slice()

    const sorted = [...safeData].sort((a, b) => {
      const result = comparator(a, b)
      return sortState.direction === 'asc' ? result : -result
    })
    return sorted
  }, [safeData, comparators, sortState])

  const pageItems = useMemo(() => {
    if (sortedData.length === 0) return [] as T[]
    const start = (page - 1) * pageSize
    const end = start + pageSize
    return sortedData.slice(start, end)
  }, [page, pageSize, sortedData])

  const handleSort = useCallback(
    (field: F) => {
      setSortState((prev) => {
        if (prev.field === field) {
          return {
            field,
            direction: prev.direction === 'desc' ? 'asc' : 'desc',
          }
        }
        return { field, direction: initialDirection }
      })
      setPage(1)
    },
    [initialDirection]
  )

  const scrollAfterPageChange = useCallback(() => {
    if (!enableScrollOnPageChange) return
    const top = isMobile ? getTopOf(mobileSelector, mobileOffset) : 0
    scrollToY(top, scrollBehavior)
  }, [enableScrollOnPageChange, mobileSelector, mobileOffset, scrollBehavior])

  const goToPreviousPage = useCallback(() => {
    setPage((prev) => {
      const next = Math.max(1, prev - 1)
      scrollAfterPageChange()
      return next
    })
  }, [scrollAfterPageChange])

  const goToNextPage = useCallback(() => {
    setPage((prev) => {
      const next = Math.min(maxPage, prev + 1)
      scrollAfterPageChange()
      return next
    })
  }, [maxPage, scrollAfterPageChange])

  return {
    pageItems,
    totalItems,
    page,
    maxPage,
    sortField: sortState.field,
    sortDirection: sortState.direction,
    handleSort,
    setPage,
    goToPreviousPage,
    goToNextPage,
  }
}
